import React from 'react';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Check, Star } from 'lucide-react';

const PricingCard = ({ plan, billingCycle }) => {
  const price = billingCycle === 'monthly' ? plan.monthlyPrice : plan.yearlyPrice;
  const isCustom = price === 'Custom';
  const originalPrice = billingCycle === 'monthly' ? plan.originalMonthlyPrice : plan.originalYearlyPrice;

  const handleButtonClick = () => {
    if (isCustom) {
      // Scroll to contact form for custom/enterprise plans
      const contactSection = document.getElementById('contact');
      if (contactSection) {
        contactSection.scrollIntoView({ behavior: 'smooth' });
      }
    } else {
      // For Basic and Pro plans, show alert or redirect to signup
      // You can replace this with actual signup/checkout logic
      alert(`You selected the ${plan.name} plan at ${plan.currency}${price}/month. Redirecting to signup...`);
      // window.location.href = '/signup'; // Uncomment when you have a signup page
    }
  };

  return (
    <Card className={`relative flex flex-col h-full transition-all duration-300 hover:shadow-xl ${
      plan.popular ? 'border-blue-600 border-2 shadow-lg' : 'border-gray-200'
    }`}>
      {plan.popular && (
        <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
          <div className="bg-blue-600 text-white px-4 py-1 rounded-full text-sm font-semibold flex items-center gap-1">
            <Star className="w-4 h-4 fill-current" />
            Most Popular
          </div>
        </div>
      )}
      
      <CardHeader className="text-center pb-4">
        <CardTitle className="text-2xl font-bold text-gray-900">{plan.name}</CardTitle>
        <CardDescription className="text-sm text-gray-600 mt-2">
          {plan.subtitle}
        </CardDescription>
      </CardHeader>

      <CardContent className="flex-grow">
        <div className="text-center mb-6 pb-6 border-b border-gray-200">
          {!isCustom ? (
            <>
              <div className="text-xl text-gray-400 line-through mb-2">
                {plan.currency}{originalPrice}
              </div>
              <div className="mb-2">
                <span className="inline-block rounded-full bg-green-100 px-3 py-1 text-xs font-semibold text-green-700">
                  Special Offer - 50% OFF
                </span>
              </div>
              <div className="flex items-baseline justify-center gap-1">
                <span className="text-4xl font-bold text-green-600">{plan.currency}{price}</span>
                <span className="text-gray-600">/month</span>
              </div>
              {billingCycle === 'yearly' && (
                <p className="text-sm text-green-600 mt-2 font-medium">Additional 10% off with yearly billing</p>
              )}
            </>
          ) : (
            <div className="text-4xl font-bold text-gray-900">Custom</div>
          )}
        </div>

        {/* Message Charges */}
        <div className="mb-6">
          <h4 className="font-semibold text-gray-900 mb-3 text-sm">Per Template Message Charges</h4>
          <div className="space-y-2">
            {plan.messageCharges.map((charge, idx) => (
              <div key={idx} className="flex justify-between items-start gap-3 text-sm">
                <span className="text-gray-700">{charge.type}:</span>
                <span className="font-medium text-gray-900 text-right">
                  {charge.price}
                </span>
              </div>
            ))}
          </div>
          <p className="mt-3 text-xs text-gray-600">
            Service: First 1,000 messages free every month, including your first month.
            After that, utility charges of ₹0.15 per message apply.
          </p>
        </div>

        {/* Features */}
        <div>
          <h4 className="font-semibold text-gray-900 mb-3 text-sm">Features</h4>
          <ul className="space-y-2">
            {plan.features.map((feature, idx) => (
              <li key={idx} className="flex items-start gap-2 text-sm">
                <Check className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                <span className="text-gray-700">{feature}</span>
              </li>
            ))}
          </ul>
        </div>
      </CardContent>

      <CardFooter className="pt-6">
        <Button 
          onClick={handleButtonClick}
          className={`w-full py-6 text-base font-semibold transition-all duration-300 ${
            plan.popular 
              ? 'bg-blue-600 hover:bg-blue-700 text-white' 
              : 'bg-gray-900 hover:bg-gray-800 text-white'
          }`}
        >
          {isCustom ? 'Contact Sales' : 'Get Started'}
        </Button>
      </CardFooter>
    </Card>
  );
};

export default PricingCard;